import { ConflictResolutionModal } from "@/components/schedule/ConflictResolutionModal";
import { DoctorSelector } from "@/components/schedule/DoctorSelector";
import { ScheduleForm } from "@/components/schedule/ScheduleForm";
import { ScheduleList } from "@/components/schedule/ScheduleList";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Doctor, Schedule } from "@/types/schedule";
import { Clock, Plus } from "lucide-react";

interface ListaHorariosMedicosProps {
  doctors: Doctor[];
  selectedDoctor: Doctor | null;
  schedules: Schedule[];
  conflictData: {
    existing: Schedule;
    new: Omit<Schedule, "id">;
  } | null;
  isScheduleModalOpen: boolean;
  scheduleToEdit: Schedule | null;
  onDoctorChange: (doctor: Doctor) => void;
  onScheduleAdd: (schedule: Omit<Schedule, "id">) => void;
  onScheduleUpdate?: (schedule: Schedule) => void;
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: string) => void;
  onConflictResolution: (action: "replace" | "keep-both" | "cancel") => void;
  onScheduleModalOpenChange: (open: boolean) => void;
  onNewSchedule?: () => void;
}

export const ListaHorariosMedicos = ({
  doctors,
  selectedDoctor,
  schedules,
  conflictData,
  isScheduleModalOpen,
  scheduleToEdit,
  onDoctorChange,
  onScheduleAdd,
  onScheduleUpdate,
  onEdit,
  onDelete,
  onConflictResolution,
  onScheduleModalOpenChange,
  onNewSchedule,
}: ListaHorariosMedicosProps) => {
  const currentDoctorSchedules = selectedDoctor
    ? schedules.filter((s) => s.doctorId === selectedDoctor.id)
    : [];

  return (
    <>
      <DoctorSelector
        doctors={doctors}
        selectedDoctor={selectedDoctor}
        onDoctorChange={onDoctorChange}
      />

      {!selectedDoctor ? (
        <Card className="p-12 text-center">
          <div className="space-y-3">
            <Clock className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
            <h2 className="text-2xl font-semibold text-foreground">
              Selecione um Médico
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Escolha um médico no menu acima para começar a gerenciar seus horários de atendimento
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-foreground">
              Horários Cadastrados
            </h2>
            <Dialog open={isScheduleModalOpen} onOpenChange={onScheduleModalOpenChange}>
              <DialogTrigger asChild>
                <Button 
                  className="gap-2"
                  onClick={onNewSchedule}
                >
                  <Plus className="h-4 w-4" />
                  Novo Horário
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {scheduleToEdit ? "Editar Horário" : "Cadastrar Novo Horário"}
                  </DialogTitle>
                </DialogHeader>
                <ScheduleForm
                  doctorId={selectedDoctor.id}
                  scheduleToEdit={scheduleToEdit}
                  onScheduleAdd={onScheduleAdd}
                  onScheduleUpdate={onScheduleUpdate}
                  onComplete={() => {
                    setTimeout(() => {
                      if (!conflictData) {
                        onScheduleModalOpenChange(false);
                      }
                    }, 100);
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>

          {currentDoctorSchedules.length > 0 ? (
            <ScheduleList
              schedules={currentDoctorSchedules}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum horário cadastrado ainda</p>
              <p className="text-sm mt-2">Clique em "Novo Horário" para começar</p>
            </div>
          )}
        </Card>
      )}

      <ConflictResolutionModal
        existingSchedule={conflictData?.existing || null}
        newSchedule={conflictData?.new || null}
        isOpen={!!conflictData}
        onResolve={onConflictResolution}
      />
    </>
  );
};

